import { deleteObject, getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { storage } from '@/config/firebase';
import { dpAttachmentRootPath, type DpAttachment, type DpAttachmentKind } from '../../../../shared/dpRequests';

export interface PendingFile {
    id: string;
    file: File;
    kind?: DpAttachmentKind;
}

function randomId(): string {
    return typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export function newPendingFile(file: File, kind?: DpAttachmentKind): PendingFile {
    return { id: randomId(), file, kind };
}

function safeFileName(name: string): string {
    return name.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-zA-Z0-9._-]+/g, '_').slice(0, 120) || 'arquivo';
}

export function buildAttachmentPath(ownerUid: string, fileName: string): string {
    return `${dpAttachmentRootPath(ownerUid)}${randomId()}/${safeFileName(fileName)}`;
}

export async function uploadPendingFiles(files: PendingFile[], ownerUid: string): Promise<DpAttachment[]> {
    const uploaded: DpAttachment[] = [];
    try {
        for (const item of files) {
            const path = buildAttachmentPath(ownerUid, item.file.name);
            const storageRef = ref(storage, path);
            await uploadBytes(storageRef, item.file, { contentType: item.file.type });
            const url = await getDownloadURL(storageRef);
            uploaded.push({
                ...(item.kind ? { kind: item.kind } : {}),
                name: item.file.name,
                url,
                path,
                contentType: item.file.type,
                size: item.file.size,
            });
        }
        return uploaded;
    } catch (error) {
        await removeUploaded(uploaded);
        throw error;
    }
}

export async function removeUploaded(attachments: DpAttachment[]): Promise<void> {
    await Promise.all(attachments.map(item => deleteObject(ref(storage, item.path)).catch(() => undefined)));
}

export async function withUploadedAttachments<T>(
    files: PendingFile[],
    ownerUid: string,
    run: (attachments: DpAttachment[]) => Promise<T>,
    alreadyUploaded: DpAttachment[] = []
): Promise<T> {
    const fresh = await uploadPendingFiles(files, ownerUid);
    try {
        return await run([...alreadyUploaded, ...fresh]);
    } catch (error) {
        await removeUploaded(fresh);
        throw error;
    }
}
